import { DisplayMessage, SystemMessageData } from "@/types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { createConsistentUsernameFromUUID } from "./userUtils";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function friendlyTimestamp(
  timestamp: number | Date,
  preferredLanguage = "en"
): string {
  const locale = preferredLanguage === "ja" ? "ja-JP" : "en-US";
  const now = new Date();
  const time = new Date(timestamp);
  const diffSec = Math.floor((now.getTime() - time.getTime()) / 1000);

  const isSameDay = now.toDateString() === time.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    time.getDate() === yesterday.getDate() &&
    time.getMonth() === yesterday.getMonth() &&
    time.getFullYear() === yesterday.getFullYear();

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  });

  // Locale-aware phrases
  const phrases: Record<string, any> = {
    en: {
      justNow: "Just now",
      minutesAgo: (n: number) => `${n} minute${n === 1 ? "" : "s"} ago`,
      todayAt: (t: string) => `Today at ${t}`,
      yesterdayAt: (t: string) => `Yesterday at ${t}`,
      dateAt: (d: string, t: string) => `${d} at ${t}`,
    },
    ja: {
      justNow: "たった今",
      minutesAgo: (n: number) => `${n}分前`,
      todayAt: (t: string) => `今日 ${t}`,
      yesterdayAt: (t: string) => `昨日 ${t}`,
      dateAt: (d: string, t: string) => `${d} ${t}`,
    },
  };

  const langPhrases = phrases[preferredLanguage] || phrases["en"];

  if (diffSec < 60) return langPhrases.justNow;
  if (diffSec < 3600) return langPhrases.minutesAgo(Math.floor(diffSec / 60));
  if (isSameDay) return langPhrases.todayAt(timeFormatter.format(time));
  if (isYesterday) return langPhrases.yesterdayAt(timeFormatter.format(time));

  return langPhrases.dateAt(
    dateFormatter.format(time),
    timeFormatter.format(time)
  );
}

export function generateShareableUrl(
  roomId: string | null,
  origin: string,
  extraParams?: Record<string, string>
): string {
  const baseUrl = `${origin}/translate`; // Clean base path
  const url = new URL(baseUrl);
  if (roomId && roomId !== "new") {
    url.searchParams.set("r", roomId);
  }
  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }
  return url.toString();
}

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers or non-secure contexts
    return new Promise((resolve, reject) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const success = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (success) {
          resolve();
        } else {
          reject(new Error("Copy command was unsuccessful."));
        }
      } catch (err) {
        document.body.removeChild(textArea);
        reject(err);
      }
    });
  }
}

declare global {
  interface Document {
    webkitFullscreenElement: any;
    webkitRequestFullscreen: any;
    webkitExitFullscreen: any;
  }

  interface HTMLElement {
    webkitRequestFullscreen: any;
  }
}

export function toggleFullscreen() {
  if (!document.fullscreenElement && !document.webkitFullscreenElement) {
    // Removed MS prefix for modern browsers
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Safari */
      elem.webkitRequestFullscreen();
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      /* Safari */
      document.webkitExitFullscreen();
    }
  }
}
/* Commenting out to avoid Google Dangerous Site block
export function generateMailtoLink(params: { email?: string, subject: string, body: string, }) {
  const subject = encodeURIComponent(params.subject);
  const body = encodeURIComponent(params.body.trim());
  return `mailto:${params.email || ""}?subject=${subject}&body=${body}`;
}

export function downloadMarkdown(filename: string, markdownContent: string) {
  const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
*/

export function generateTextTranscript(roomId: string, messages: (DisplayMessage | SystemMessageData)[], targetLanguage: string, locale?: string) {
  const optionsDate = { year: 'numeric', month: 'long', day: 'numeric' } as const;
  const optionsTime = { hour: 'numeric', minute: '2-digit', hour12: true } as const;

  const shareURL = generateShareableUrl(
    roomId,
    window.location.origin,
  );

  let lastDate: string | null = null;
  const formatLine = (message: DisplayMessage | SystemMessageData) => {
    const dateObj = new Date(message.timestamp);
    const timeStr = dateObj.toLocaleTimeString(locale, optionsTime);
    const displayName = message.displayUsername || createConsistentUsernameFromUUID(message.username);
    let transcript = "";
    if (message.type == "message") {
      transcript = `**[${timeStr}]** **${displayName}**: "${message.message}"\n>${(message.translations as any)?.[targetLanguage]}`;
    } else if (message.type == "system") {
      if (message.message == "JOIN") {
        transcript = `**[${timeStr}]** *${displayName} joined the chat.*`;
      }
      if (message.message == "LEAVE") {
        transcript = `**[${timeStr}]** *${displayName} left the chat.*`;
      }
    }
    return `\n${transcript}`;
  }
  const lines: string[] = [];
  messages.slice().reverse().forEach(message => {
    const dateObj = new Date(message.timestamp);
    const dateStr = dateObj.toLocaleDateString(locale, optionsDate);
    if (dateStr !== lastDate) {
      lines.push(`\n## ${dateStr}`);
      lastDate = dateStr;
    }
    const lineTxt = formatLine(message);
    lines.push(lineTxt);
  })
  const body = lines.join("\n");
  const header = `# Transcript for Chotto Chat - Room ID: ${roomId} \n [Open Chat](${shareURL})\n`;
  return header
    // + "\n--------------------------------------------------------------------------------------------------------\n\n"
    + body;
}

/**
 * URL where original audio can be downloaded.
 */
export function getSourceAudioURL(msgId: string, roomId: string, username: string) {
  const audioUrl = `/chat/download-audio?ts=${msgId}&rid=${roomId}&user=${username}`;
  return audioUrl;
}
